/**
 * Build the O(1) lookup used by reply quote rendering.
 *
 * Ordinary messages are indexed by their exact external_message_id.
 *
 * MAX albums are special:
 *
 *   real MAX message:
 *     max_mid.X
 *
 *   locally expanded rows:
 *     max_mid.X_attachment_1
 *     max_mid.X_attachment_2
 *
 *   shared media group:
 *     max_mid.X_album
 *
 * MAX native replies reference the real message ID (`max_mid.X`), while
 * our database intentionally contains only the synthetic attachment rows.
 * Register the real ID as an alias to one representative album row so a
 * native MAX album reply can render a useful quote.
 *
 * An exact real row always wins over a synthetic alias.
 */
export function buildMessagesByExternalId(messages = []) {
  const result = new Map();
  const albumRepresentatives = new Map();

  for (const message of messages) {
    if (!message || typeof message !== 'object') {
      continue;
    }

    const externalId = (
      typeof message.external_message_id === 'string'
        ? message.external_message_id
        : ''
    );

    if (!externalId) {
      continue;
    }

    // Preserve the existing exact-ID contract.
    result.set(externalId, message);

    const match = /^(max_.+)_attachment_\d+$/.exec(
      externalId,
    );

    if (!match) {
      continue;
    }

    const realMessageId = match[1];

    // MAX Bot historical expansion uses <real-id>_album.
    // MAX Personal outgoing albums keep the HUB-generated media_group_id,
    // while each local attachment gets a synthetic remote-compatible id.
    const nativeExpandedAlbum = (
      message.media_group_id
      === `${realMessageId}_album`
    );

    const hubPersonalAlbum = (
      realMessageId.startsWith('max_personal_')
      && typeof message.media_group_id === 'string'
      && message.media_group_id.startsWith('hub_group_')
    );

    if (
      !nativeExpandedAlbum
      && !hubPersonalAlbum
    ) {
      continue;
    }

    const current = albumRepresentatives.get(
      realMessageId,
    );

    // Prefer the row that owns the album caption. If neither has text,
    // retain the first encountered attachment as the preview.
    if (
      !current
      || (!current.text && message.text)
    ) {
      albumRepresentatives.set(
        realMessageId,
        message,
      );
    }
  }

  for (
    const [realMessageId, representative]
    of albumRepresentatives
  ) {
    // Future-proofing: if a real row ever exists, never replace it with
    // the compatibility alias.
    if (!result.has(realMessageId)) {
      result.set(
        realMessageId,
        representative,
      );
    }
  }

  return result;
}
